import React, { useEffect, useRef, useState } from "react"
import { Button, Col, Flex, Form, Input, InputNumber, Row, Radio as AntdRadio } from "antd"
import { InfoCircleOutlined, PlusOutlined } from "@ant-design/icons"
import { useMediaQuery } from "react-responsive"
import { useTranslation } from "react-i18next"
import { Card } from "ui/Card"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import { TicketTypeRow } from "../components/ticketType/TicketTypeRow"
import { TicketTypeCard } from "../components/ticketType/TicketTypeCard"
import { CreateTicketTypeModal } from "../components/ticketType/CreateTicketTypeModal/CreateTicketTypeModal"
import styles from "./TicketTypeStep.module.css"
import { Text } from "ui/Typography"
import { Radio } from "ui/Radio"
import { useWizardContext } from "../WizardContext"
import { buildTicketTypePayload } from "../mappers/toApiPayload"

const { TextArea } = Input

export const TicketTypeStep = () => {
  const { t } = useTranslation()
  const { stepValidateHandlersRef, stepSaveHandlersRef, form, isFormInitialized } = useWizardContext()
  const isWideScreen = useMediaQuery({ query: "(min-width: 1281px)" })
  const [ticketTypes, setTicketTypes] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingTicket, setEditingTicket] = useState(null)
  const hasLocalInit = useRef(false)
  const ticketDuration = Form.useWatch("duration_type", form)
  const isMultiDay = ticketDuration === "multi_day"

  // isFormInitialized becomes true only after WizardProvider has called form.setFieldsValue().
  // By the time this effect fires, the form already has the API data — no effect ordering risk.
  useEffect(() => {
    if (!isFormInitialized || hasLocalInit.current) return
    hasLocalInit.current = true
    const fromForm = form.getFieldValue("ticket_types") || []
    setTicketTypes(fromForm)
    // No need to write back to form — it already has this value from WizardProvider.
  }, [isFormInitialized, form])

  useEffect(() => {
    const handlers = stepValidateHandlersRef.current
    handlers["ticket_type"] = async () => {
      if (ticketTypes.length === 0) {
        const err = new Error("ticket_types_required")
        err.errorFields = [{ errors: [t("experiences.ticket_type.at_least_one_required")] }]
        throw err
      }
    }
    return () => {
      delete handlers["ticket_type"]
    }
  }, [ticketTypes, stepValidateHandlersRef, t])

  useEffect(() => {
    stepSaveHandlersRef.current["ticket_type"] = async () => {
      return buildTicketTypePayload({ ...form.getFieldsValue(), ticket_types: ticketTypes })
    }
    return () => {
      delete stepSaveHandlersRef.current["ticket_type"]
    }
  }, [stepSaveHandlersRef, form, ticketTypes])

  // Helpers that update both local state and form together — form stays in sync for the save handler.
  const updateTicketTypes = (next) => {
    setTicketTypes(next)
    form.setFieldValue("ticket_types", next)
  }

  const handleOpenCreate = () => {
    setEditingTicket(null)
    setIsModalOpen(true)
  }

  const handleEdit = (ticket) => {
    setEditingTicket(ticket)
    setIsModalOpen(true)
  }

  const handleSave = ({ title, description, maxCapacity }) => {
    if (editingTicket) {
      updateTicketTypes(ticketTypes.map((t) => (t.id === editingTicket.id ? { ...t, title, description, maxCapacity } : t)))
    } else {
      updateTicketTypes([...ticketTypes, { id: Date.now(), title, description, maxCapacity, enabled: true }])
    }
  }

  const handleModalClose = () => {
    setEditingTicket(null)
    setIsModalOpen(false)
  }

  // Same invariant as Base Prices: at least one ticket type must stay enabled,
  // since prices are set for enabled types on the Pricing step.
  const enabledCount = ticketTypes.filter((t) => !t._destroy && t.enabled !== false).length

  const handleToggle = (id, checked) => {
    if (!checked && enabledCount <= 1) return
    updateTicketTypes(ticketTypes.map((t) => (t.id === id ? { ...t, enabled: checked } : t)))
    // Mirror into the Base Prices state so the Pricing tab toggle matches this one
    const prices = form.getFieldValue("ticketTypePrices") || {}
    form.setFieldValue("ticketTypePrices", { ...prices, [id]: { ...prices[id], enabled: checked } })
  }

  const handleDelete = (id) => {
    // Temp IDs (Date.now()) → drop entirely; server IDs → mark _destroy so Rails deletes them
    const isServerId = (ticketId) => typeof ticketId === "number" && ticketId < 1e12
    if (isServerId(id)) {
      updateTicketTypes(ticketTypes.map((t) => (t.id === id ? { ...t, _destroy: true } : t)))
    } else {
      updateTicketTypes(ticketTypes.filter((t) => t.id !== id))
    }
    // Drop the orphaned base price entry so the pricing payload doesn't reference a deleted type
    // Spreading undefined is safe — yields an empty object
    const remainingPrices = { ...form.getFieldValue("ticketTypePrices") }
    delete remainingPrices[id]
    form.setFieldValue("ticketTypePrices", remainingPrices)
  }

  return (
    <div>
      <Card title={"Ticket Information"} className="mb-xl pb-m">
        <Flex vertical gap={16} className="border-b mb-m">
          <Text strong>{"Duration"}</Text>
          <Form.Item name="duration_type" initialValue="single_day" noStyle>
            <AntdRadio.Group className="mb-m">
              <Radio value="single_day">{"Single Day"}</Radio>
              <Radio value="multi_day">{"Multi-Day"}</Radio>
            </AntdRadio.Group>
          </Form.Item>
          {isMultiDay && (
            <Flex align="center" gap={16} className="mb-m">
              <Text strong>{"Number of Days"}</Text>
              <Form.Item
                name="number_of_days"
                rules={[{ required: true, message: t("experiences.ticket_type.number_of_days_required") }, { type: "number", min: 2, message: t("experiences.ticket_type.min_days_error") }]}
                style={{ marginBottom: 0 }}
              >
                <InputNumber min={2} precision={0} parser={(val) => val?.replaceAll(/\D/g, "")} style={{ width: 174 }} size="large" />
              </Form.Item>
            </Flex>
          )}
        </Flex>

        <Flex vertical gap={8} className="mb-m">
          <Text strong>{"Number of tickets"}</Text>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="min_tickets"
                label="Minimum"
                colon={false}
                style={{ marginBottom: 0 }}
                rules={[
                  { required: true, message: t("experiences.ticket_type.min_tickets_required") },
                  { type: "number", min: 1, message: t("experiences.ticket_type.min_tickets_greater_than_zero") }
                ]}
              >
                {/* No min prop — antd would clamp 0 to the min on blur; the rule above shows an error instead */}
                <InputNumber placeholder="ex. 1" min={0} precision={0} parser={(val) => val?.replaceAll(/\D/g, "")} style={{ width: "100%" }} size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="max_tickets"
                label="Maximum"
                colon={false}
                style={{ marginBottom: 0 }}
                rules={[
                  { required: true, message: t("experiences.ticket_type.max_tickets_required") },
                  { type: "number", min: 1, message: t("experiences.ticket_type.max_tickets_greater_than_zero") },
                  {
                    validator: (_, value) => {
                      const min = form.getFieldValue("min_tickets")
                      if (value != null && min != null && value < min) {
                        return Promise.reject(t("experiences.ticket_type.max_tickets_greater_than_min"))
                      }
                      return Promise.resolve()
                    }
                  }
                ]}
              >
                <InputNumber placeholder="ex. 30" min={0} precision={0} parser={(val) => val?.replaceAll(/\D/g, "")} style={{ width: "100%" }} size="large" />
              </Form.Item>
            </Col>
          </Row>
        </Flex>

        <Form.Item
          name="ticket_description"
          label="Description of the ticket"
          colon={false}
          style={{ marginBottom: 0 }}
          rules={[{ required: true, message: t("experiences.ticket_type.ticket_description_required") }]}
        >
          <TextArea placeholder="Enter description here" rows={4} size="large" />
        </Form.Item>
      </Card>

      <Card
        title={"Ticket Types"}
        className="mb-xl"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate} className={styles.addNewBtn}>
            {"Add New"}
          </Button>
        }
      >
        <WithEmptyStateWrapper
          name="ticket type"
          hasItems={ticketTypes.some((t) => !t._destroy)}
          fallbackButtonClickHandler={handleOpenCreate}
        >
          <Flex vertical gap={8}>
            {ticketTypes.filter((t) => !t._destroy).map((ticket, _, visible) => {
              const isLastEnabled = ticket.enabled !== false && enabledCount <= 1
              return isWideScreen ? (
                <TicketTypeRow key={ticket.id} ticket={ticket} isLast={visible.length === 1} isLastEnabled={isLastEnabled} onToggle={handleToggle} onEdit={handleEdit} onDelete={handleDelete} />
              ) : (
                <TicketTypeCard key={ticket.id} ticket={ticket} isLast={visible.length === 1} isLastEnabled={isLastEnabled} onToggle={handleToggle} onEdit={handleEdit} onDelete={handleDelete} />
              )
            })}
          </Flex>
        </WithEmptyStateWrapper>
        <Flex align="center" gap={6} className={styles.priceInfo}>
          <InfoCircleOutlined className={styles.infoIcon} />
          <Text className={styles.priceInfoText}>{"Prices will be set on next step"}</Text>
        </Flex>
      </Card>

      <CreateTicketTypeModal
        open={isModalOpen}
        onCancel={handleModalClose}
        onSave={handleSave}
        initialValues={editingTicket ? { title: editingTicket.title, description: editingTicket.description, maxCapacity: editingTicket.maxCapacity } : null}
      />
    </div>
  )
}
